/* eslint-disable import/no-cycle */
import { createActions } from 'redux-actions';
import { selectPageLoadState } from '../AppStateV2PageLoadState/selectors';
import {
	selectChartPrimarySurveyId,
	selectChartDepolSurveyId,
	selectLessNegativeThreshold,
	selectMoreNegativeThreshold,
	selectUseThreshold,
	selectUseShadow,
	selectUseInterpolatedDepol,
	selectUseNormalizedDepol,
	selectBelowCriterionComparator,
	selectSeriesVisibilityDataStoreForChart,
	selectChartsSettings
} from '../Charts/slices/ChartsSettings/selectors';

import {
	selectCustomGroupRanges,
	selectRemediationActions
} from '../Charts/slices/ChartsRemediations/selectors';
import scanlineUtils from '../../../scanlineUtils';
import { selectAppStateData } from './selectors';

const {
	receiveAppState,
	setAppStateFromChartSettings,
	setIsSavingAppState
} = createActions(
	{
		RECEIVE_APP_STATE: appState => ({ appState }),
		SET_APP_STATE_FROM_CHART_SETTINGS: appState => ({ appState }),
		SET_IS_SAVING_APP_STATE: isSavingAppState => ({ isSavingAppState })
	},
	{ prefix: 'AppStateV2' }
);

const _removeSeriesCache = (sliceChartsSettings = {}) => {
	const chartIds = Object.keys(sliceChartsSettings);
	const modifiedSliceChartsSettings = chartIds.reduce((acc, chartId) => {
		const clone = {...(sliceChartsSettings[chartId] || {})};
		delete clone.seriesCache;
		acc[chartId] = clone;
		return acc;
	}, {});
	return modifiedSliceChartsSettings;
}

const loadAppStateV2FromPageLoadState = () => (dispatch, getState) => {
	const { app } = getState();
	const pageLoadSteate = selectPageLoadState(app);
	dispatch(receiveAppState(pageLoadSteate || {}));
};

const thunkSetAppStateFromChartSettings = chartId => (dispatch, getState) => {
	const { app: stateApp } = getState();
	const identifiers = { chartId };

	const primarySurveyId = selectChartPrimarySurveyId(stateApp, identifiers);
	const depolSurveyId = selectChartDepolSurveyId(stateApp, identifiers);
	const lessNegativeThreshold = selectLessNegativeThreshold(stateApp, identifiers);
	const moreNegativeThreshold = selectMoreNegativeThreshold(stateApp, identifiers);

	const bcComparator = selectBelowCriterionComparator(stateApp, identifiers);
	const useThreshold = selectUseThreshold(stateApp, identifiers);
	const useShadow = selectUseShadow(stateApp, identifiers);
	const useInterpolatedDepol = selectUseInterpolatedDepol(
		stateApp,
		identifiers
	);
	const useNormalizedDepol = selectUseNormalizedDepol(stateApp, identifiers);

	const customGroups = selectCustomGroupRanges(stateApp, chartId);
	const remediationActions = selectRemediationActions(stateApp, chartId);

	const seriesVisibility = selectSeriesVisibilityDataStoreForChart(stateApp, {
		chartId
	});

	const sliceChartsSettings = selectChartsSettings(stateApp);

	const appState = {
		_sliceChartsSettings: _removeSeriesCache(sliceChartsSettings),

		chartId,
		surveyId: primarySurveyId,
		primarySurveyId,
		depolSurveyId,
		lessNegativeThreshold,
		moreNegativeThreshold,

		bcComparator,
		useThreshold,
		useShadow,
		useInterpolatedDepol,
		useNormalizedDepol,

		seriesVisibility,

		customGroups,
		remediationActions: {
			[primarySurveyId]: remediationActions
		},
	};

	dispatch(setAppStateFromChartSettings(appState));
};

const delayedSaveAppStateV2 = (lineId, projectId, getState, onSaveComplete) => {
	return new Promise((resolve, reject) => {
		setTimeout(() => {
			const { app: stateApp } = getState();
			const appStateV2 = selectAppStateData(stateApp);

			scanlineUtils
				.saveAppStateV2({ lineId, projectId, data: { content: appStateV2 } })
				.then(res => {
					onSaveComplete();
					resolve(res);
				})
				.catch(err => {
					onSaveComplete();
					reject(err);
				});
		}, 1);
	});
};

const thunkPromiseSaveAppSettings = ({ chartId, lineId, projectId }) => (
	dispatch,
	getState
) => {
	dispatch(thunkSetAppStateFromChartSettings(chartId));
	dispatch(setIsSavingAppState(true));
	const onSaveComplete = () => {
		dispatch(setIsSavingAppState(false));
	};
	return delayedSaveAppStateV2(lineId, projectId, getState, onSaveComplete);
};

export {
	loadAppStateV2FromPageLoadState,
	receiveAppState,
	setAppStateFromChartSettings,
	setIsSavingAppState,
	thunkPromiseSaveAppSettings
};
